<template>
  <main-template>
    <!-- Passo 1: escolher censo -->
    <template v-if="!censoSelecionado">
      <v-row class="ma-4 mb-0">
        <v-col>
          <div class="d-flex align-center" style="gap: 8px">
            <v-btn icon x-small @click="voltarTelaAnterior">
              <v-icon>fa fa-angle-left</v-icon>
            </v-btn>
            <div>
              <h1 class="mb-0" style="line-height: 1.2">Preenchimento do Censo</h1>
              <div class="text-caption grey--text">
                Selecione o censo para iniciar o preenchimento
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
      <v-row v-if="loading" class="ma-4 mt-0">
        <v-col><v-progress-linear indeterminate color="primary" /></v-col>
      </v-row>
      <v-row v-else class="ma-4 mt-0">
        <v-col cols="12">
          <div class="text-overline font-weight-bold mb-3 grey--text text--darken-1">
            CENSOS DISPONÍVEIS
          </div>
        </v-col>
        <v-col v-for="censo in censos" :key="censo.id" cols="12" sm="6" md="4">
          <v-card
            outlined
            class="rounded-xl pa-4"
            style="cursor: pointer"
            @click="selecionarCenso(censo)"
          >
            <div class="d-flex justify-space-between align-center mb-2">
              <v-chip x-small :color="censo.finalizado ? 'green' : 'orange'" dark>
                {{ censo.finalizado ? "Finalizado" : "Aberto" }}
              </v-chip>
              <span class="text-caption grey--text">{{
                censo.ano ? censo.ano.descricao : "—"
              }}</span>
            </div>
            <div class="text-subtitle-1 font-weight-bold mb-1">{{ censo.descricao }}</div>
            <div class="text-caption grey--text">
              {{ censo.etapas ? censo.etapas.length : 0 }} etapas
            </div>
            <div class="d-flex justify-end mt-3">
              <v-btn x-small text color="primary">
                Selecionar <v-icon x-small right>fa fa-arrow-right</v-icon>
              </v-btn>
            </div>
          </v-card>
        </v-col>
        <v-col v-if="censos.length === 0" cols="12">
          <div class="text-caption grey--text">Nenhum censo disponível.</div>
        </v-col>
      </v-row>
    </template>

    <!-- Passo 2: escolher etapa -->
    <template v-else-if="!etapaSelecionada">
      <v-row class="ma-4 mb-0">
        <v-col>
          <div class="d-flex align-center" style="gap: 8px">
            <v-btn icon x-small @click="censoSelecionado = null">
              <v-icon>fa fa-angle-left</v-icon>
            </v-btn>
            <div>
              <h1 class="mb-0" style="line-height: 1.2">
                {{ censoSelecionado.descricao }}
              </h1>
              <div class="text-caption grey--text">Selecione uma etapa para preencher</div>
            </div>
          </div>
        </v-col>
      </v-row>
      <v-row class="ma-4 mt-0">
        <v-col cols="12">
          <div class="text-overline font-weight-bold mb-3 grey--text text--darken-1">ETAPAS</div>
        </v-col>
        <v-col v-for="(etapa, index) in censoSelecionado.etapas" :key="etapa.id" cols="12" sm="6">
          <v-card
            outlined
            class="rounded-xl pa-4"
            style="cursor: pointer"
            @click="selecionarEtapa(etapa)"
          >
            <div class="d-flex justify-space-between align-center mb-2">
              <v-chip x-small label color="primary" class="white--text font-weight-bold">
                ETAPA {{ index + 1 }}
              </v-chip>
              <v-chip x-small :color="etapa.situacao === 'fechada' ? 'green' : 'orange'" dark>
                {{ etapa.situacao === "fechada" ? "Finalizada" : "Em coleta" }}
              </v-chip>
            </div>
            <div class="text-subtitle-1 font-weight-bold">{{ etapa.descricao }}</div>
            <div class="d-flex justify-end mt-3">
              <v-btn x-small text color="primary">
                Preencher <v-icon x-small right>fa fa-arrow-right</v-icon>
              </v-btn>
            </div>
          </v-card>
        </v-col>
        <v-col v-if="!censoSelecionado.etapas || censoSelecionado.etapas.length === 0" cols="12">
          <div class="text-caption grey--text">Nenhuma etapa disponível.</div>
        </v-col>
      </v-row>
    </template>

    <!-- Passo 3: preencher — layout com tabs no topo colorido -->
    <template v-else>
      <v-row class="ma-4 mb-0">
        <v-col>
          <div class="d-flex align-center justify-space-between">
            <div class="d-flex align-center" style="gap: 8px">
              <v-btn icon x-small @click="etapaSelecionada = null">
                <v-icon>fa fa-angle-left</v-icon>
              </v-btn>
              <div>
                <h1 class="mb-0" style="line-height: 1.2">{{ etapaSelecionada.descricao }}</h1>
                <div class="text-caption grey--text">{{ censoSelecionado.descricao }}</div>
              </div>
            </div>
            <v-btn color="primary" :loading="gerandoArquivo" @click="gerarArquivo">
              <v-icon left small>fa fa-download</v-icon>
              Gerar arquivo
            </v-btn>
          </div>
        </v-col>
      </v-row>
      <v-row class="ma-4">
        <v-col cols="12">
          <div v-if="loadingEtapa">
            <v-progress-linear indeterminate color="primary" />
          </div>
          <v-card v-else-if="etapaDetalhada" outlined>
            <v-tabs
              v-model="moduloAtivo"
              background-color="primary"
              dark
              centered
              icons-and-text
              show-arrows
            >
              <v-tab v-for="modulo in etapaDetalhada.modulos" :key="modulo.id">
                {{ modulo.descricao }}
                <v-icon>fa fa-list</v-icon>
              </v-tab>
            </v-tabs>

            <v-tabs-items
              v-model="moduloAtivo"
              style="overflow-y: auto; max-height: calc(100vh - 220px)"
            >
              <v-tab-item v-for="modulo in etapaDetalhada.modulos" :key="modulo.id">
                <!-- Módulo 20: turmas -->
                <template v-if="isTurmaModulo(modulo)">
                  <div style="position: sticky; top: 0; z-index: 2">
                    <v-tabs
                      v-model="turmaAtiva"
                      background-color="blue-grey lighten-5"
                      color="primary"
                      show-arrows
                      class="elevation-0"
                    >
                      <v-tab v-for="turma in turmas" :key="turma.id" class="text-caption">
                        {{ turma.descricao }}
                      </v-tab>
                    </v-tabs>
                    <v-divider />
                  </div>
                  <v-tabs-items v-model="turmaAtiva">
                    <v-tab-item v-for="turma in turmas" :key="turma.id">
                      <v-card flat class="pa-4">
                        <template v-if="modulo.campos && modulo.campos.length > 0">
                          <v-card
                            v-for="grupo in camposPorGrupo(modulo.campos)"
                            v-if="grupoTemCamposVisiveis(grupo, respostas[turma.id] || {})"
                            :key="grupo.nome"
                            outlined
                            class="mb-4"
                          >
                            <v-card-subtitle class="font-weight-bold pt-2 mb-3 pb-0 text-uppercase">
                              {{ grupo.nome }}
                            </v-card-subtitle>
                            <v-card-text class="pt-1 pb-2">
                              <v-row>
                                <v-col
                                  v-for="campo in grupo.campos"
                                  v-if="campoDependenciaAtendida(campo, respostas[turma.id] || {})"
                                  :key="campo.id"
                                  cols="12"
                                  sm="6"
                                  class="py-1"
                                >
                                  <ValidationProvider
                                    :name="campo.nome"
                                    :rules="regrasCampo(campo)"
                                    v-slot="{ errors }"
                                  >
                                    <v-textarea
                                      v-if="campo.tipo === 'textarea'"
                                      v-model="respostas[turma.id][campo.id]"
                                      :label="campo.nome"
                                      :placeholder="campo.placeholder || ''"
                                      :error-messages="
                                        errors.length
                                          ? errors
                                          : errosTurma[turma.id]?.[campo.id] || []
                                      "
                                      @input="limparErroTurma(turma.id, campo.id)"
                                      outlined
                                      dense
                                      rows="3"
                                      auto-grow
                                    />
                                    <v-checkbox
                                      v-else-if="campo.tipo === 'checkbox'"
                                      v-model="respostas[turma.id][campo.id]"
                                      :label="campo.nome"
                                      :error-messages="
                                        errors.length
                                          ? errors
                                          : errosTurma[turma.id]?.[campo.id] || []
                                      "
                                      @change="limparErroTurma(turma.id, campo.id)"
                                      dense
                                      class="mt-0"
                                    />
                                    <v-select
                                      v-else-if="campo.tipo === 'select'"
                                      v-model="respostas[turma.id][campo.id]"
                                      :label="campo.nome"
                                      :items="opcoesFormatadas(campo.opcoes)"
                                      :placeholder="campo.placeholder || ''"
                                      :error-messages="
                                        errors.length
                                          ? errors
                                          : errosTurma[turma.id]?.[campo.id] || []
                                      "
                                      @change="limparErroTurma(turma.id, campo.id)"
                                      outlined
                                      clearable
                                      dense
                                    />
                                    <v-text-field
                                      v-else
                                      v-model="respostas[turma.id][campo.id]"
                                      :label="campo.nome"
                                      :placeholder="campo.placeholder || ''"
                                      :type="
                                        campo.tipo === 'number'
                                          ? 'number'
                                          : campo.tipo === 'date'
                                          ? 'date'
                                          : 'text'
                                      "
                                      :error-messages="
                                        errors.length
                                          ? errors
                                          : errosTurma[turma.id]?.[campo.id] || []
                                      "
                                      @input="limparErroTurma(turma.id, campo.id)"
                                      outlined
                                      dense
                                    />
                                  </ValidationProvider>
                                </v-col>
                              </v-row>
                            </v-card-text>
                          </v-card>
                        </template>
                        <div v-else class="text-caption grey--text py-2 text-center">
                          Nenhum campo configurado neste módulo.
                        </div>
                      </v-card>
                    </v-tab-item>
                  </v-tabs-items>
                  <div class="d-flex justify-end px-4 py-3">
                    <v-btn color="info" outlined class="mr-2" @click="sincronizarModulo(modulo)">
                      <v-icon left small>fa fa-sync</v-icon>
                      Sincronizar com o sistema
                    </v-btn>
                    <v-btn color="error" outlined class="mr-2" @click="excluirDadosModulo(modulo)">
                      <v-icon left small>fa fa-trash</v-icon>
                      Excluir dados
                    </v-btn>
                    <v-btn
                      color="primary"
                      :loading="salvandoModulo === `${modulo.id}-todas-turmas`"
                      @click="salvarTodasTurmas(modulo)"
                    >
                      <v-icon left small>fa fa-save</v-icon>
                      Salvar todas as turmas
                    </v-btn>
                  </div>
                </template>

                <!-- Módulo 30: pessoas físicas -->
                <template v-else-if="isPessoaFisicaModulo(modulo)">
                  <div class="d-flex justify-end align-center px-4 py-2">
                    <v-radio-group
                      v-model="pessoaFisicaTipo"
                      row
                      dense
                      hide-details
                      class="mt-2 MB-1"
                      @change="trocarTipoPessoa"
                    >
                      <v-radio label="Professor" value="professor" />
                      <v-radio label="Aluno" value="aluno" />
                      <v-radio label="Gestor" value="gestor" />
                    </v-radio-group>
                  </div>
                  <v-divider />
                  <div
                    v-if="pessoasAtuais.length === 0"
                    class="text-caption grey--text py-4 text-center"
                  >
                    Nenhuma pessoa encontrada.
                  </div>
                  <template v-else>
                    <div class="pa-4">
                      <v-card
                        v-for="pessoa in pessoasPaginadas"
                        :key="`${pessoaFisicaTipo}-${pessoa.id}`"
                        :id="cardElId(30, pessoa.id)"
                        outlined
                        class="mb-4"
                        :class="{ 'destaque-pendencia': isDestacado(30, pessoa.id) }"
                      >
                        <!-- <v-card-title class="subtitle-2 py-2 px-4 blue-grey lighten-5">
                          {{ pessoaNome(pessoa) }}
                        </v-card-title> -->
                        <v-divider />
                        <v-tabs
                          v-model="subTabsPessoas[`${pessoaFisicaTipo}_${pessoa.id}`]"
                          background-color="primary"
                          dark
                          show-arrows
                          class="elevation-0 mb-6"
                        >
                          <v-tab
                            v-for="grupo in pessoaFisicaSubTabs(modulo.campos)"
                            :key="grupo.nome"
                            class="text-caption"
                          >
                            {{ grupo.nome }}
                          </v-tab>
                        </v-tabs>
                        <ValidationObserver
                          :key="`obs-${pessoaFisicaTipo}-${pessoa.id}`"
                          v-slot="{ handleSubmit }"
                        >
                          <v-tabs-items
                            v-model="subTabsPessoas[`${pessoaFisicaTipo}_${pessoa.id}`]"
                          >
                            <v-tab-item
                              v-for="grupo in pessoaFisicaSubTabs(modulo.campos)"
                              :key="grupo.nome"
                            >
                              <v-card-text>
                                <template v-for="sub in camposPorGrupo(grupo.campos)">
                                  <v-card-subtitle
                                    v-if="
                                      camposPorGrupo(grupo.campos).length > 1 &&
                                      grupoTemCamposVisiveis(
                                        sub,
                                        respostasPessoas[`${pessoaFisicaTipo}_${pessoa.id}`] || {}
                                      )
                                    "
                                    :key="`h-${sub.nome}`"
                                    class="font-weight-bold pt-2 mb-2 pb-0 text-uppercase"
                                  >
                                    {{ sub.nome }}
                                  </v-card-subtitle>
                                  <v-row :key="`r-${sub.nome}`">
                                    <v-col
                                      v-for="campo in sub.campos"
                                      v-if="
                                        campoDependenciaAtendida(
                                          campo,
                                          respostasPessoas[`${pessoaFisicaTipo}_${pessoa.id}`] || {}
                                        )
                                      "
                                      :key="campo.id"
                                      cols="12"
                                      sm="6"
                                      class="py-1"
                                    >
                                      <ValidationProvider
                                        :name="campo.nome"
                                        :rules="regrasCampo(campo, pessoaFisicaTipo)"
                                        v-slot="{ errors }"
                                      >
                                        <v-textarea
                                          v-if="campo.tipo === 'textarea'"
                                          v-model="
                                            respostasPessoas[`${pessoaFisicaTipo}_${pessoa.id}`][
                                              campo.id
                                            ]
                                          "
                                          :label="campo.nome"
                                          :placeholder="campo.placeholder || ''"
                                          :error-messages="
                                            errors.length
                                              ? errors
                                              : errosPessoas[`${pessoaFisicaTipo}_${pessoa.id}`]?.[
                                                  campo.id
                                                ] || []
                                          "
                                          @input="
                                            limparErroPessoa(pessoaFisicaTipo, pessoa.id, campo.id)
                                          "
                                          outlined
                                          dense
                                          rows="3"
                                          auto-grow
                                        />
                                        <v-checkbox
                                          v-else-if="campo.tipo === 'checkbox'"
                                          v-model="
                                            respostasPessoas[`${pessoaFisicaTipo}_${pessoa.id}`][
                                              campo.id
                                            ]
                                          "
                                          :label="campo.nome"
                                          :error-messages="
                                            errors.length
                                              ? errors
                                              : errosPessoas[`${pessoaFisicaTipo}_${pessoa.id}`]?.[
                                                  campo.id
                                                ] || []
                                          "
                                          @change="
                                            limparErroPessoa(pessoaFisicaTipo, pessoa.id, campo.id)
                                          "
                                          dense
                                          class="mt-0"
                                        />
                                        <v-select
                                          v-else-if="campo.tipo === 'select'"
                                          v-model="
                                            respostasPessoas[`${pessoaFisicaTipo}_${pessoa.id}`][
                                              campo.id
                                            ]
                                          "
                                          :label="campo.nome"
                                          :items="opcoesFormatadas(campo.opcoes)"
                                          :placeholder="campo.placeholder || ''"
                                          :error-messages="
                                            errors.length
                                              ? errors
                                              : errosPessoas[`${pessoaFisicaTipo}_${pessoa.id}`]?.[
                                                  campo.id
                                                ] || []
                                          "
                                          @change="
                                            limparErroPessoa(pessoaFisicaTipo, pessoa.id, campo.id)
                                          "
                                          outlined
                                          clearable
                                          dense
                                        />
                                        <v-text-field
                                          v-else
                                          v-model="
                                            respostasPessoas[`${pessoaFisicaTipo}_${pessoa.id}`][
                                              campo.id
                                            ]
                                          "
                                          :label="campo.nome"
                                          :placeholder="campo.placeholder || ''"
                                          :type="
                                            campo.tipo === 'number'
                                              ? 'number'
                                              : campo.tipo === 'date'
                                              ? 'date'
                                              : 'text'
                                          "
                                          :error-messages="
                                            errors.length
                                              ? errors
                                              : errosPessoas[`${pessoaFisicaTipo}_${pessoa.id}`]?.[
                                                  campo.id
                                                ] || []
                                          "
                                          @input="
                                            limparErroPessoa(pessoaFisicaTipo, pessoa.id, campo.id)
                                          "
                                          outlined
                                          dense
                                        />
                                      </ValidationProvider>
                                    </v-col>
                                  </v-row>
                                </template>
                                <div
                                  v-if="
                                    !grupoTemCamposVisiveis(
                                      grupo,
                                      respostasPessoas[`${pessoaFisicaTipo}_${pessoa.id}`] || {}
                                    )
                                  "
                                  class="text-caption grey--text py-2 text-center"
                                >
                                  Preencha o campo obrigatório para liberar esta seção.
                                </div>
                              </v-card-text>
                            </v-tab-item>
                          </v-tabs-items>
                          <!-- <v-card-actions class="justify-end">
                            <v-btn
                              color="primary"
                              small
                              :loading="salvandoModulo === `${modulo.id}-${pessoaFisicaTipo}-${pessoa.id}`"
                              @click="handleSubmit(() => salvarRespostas(modulo, null, null, pessoa), () => $toast.danger('Preencha os campos obrigatórios antes de salvar.'))"
                            >
                              <v-icon left small>fa fa-save</v-icon>
                              Salvar
                            </v-btn>
                          </v-card-actions> -->
                        </ValidationObserver>
                      </v-card>
                    </div>
                    <div class="d-flex justify-center pb-4">
                      <v-pagination
                        v-model="pessoaPaginaAtual"
                        :length="Math.ceil(pessoasAtuais.length / pessoasPorPagina)"
                        total-visible="7"
                      />
                    </div>
                  </template>
                  <div class="d-flex justify-end px-4 py-3">
                    <v-btn color="info" outlined class="mr-2" @click="sincronizarModulo(modulo)">
                      <v-icon left small>fa fa-sync</v-icon>
                      Sincronizar com o sistema
                    </v-btn>
                    <v-btn
                      color="error"
                      outlined
                      class="mr-2"
                      @click="excluirDadosModulo(modulo, pessoaFisicaTipo)"
                    >
                      <v-icon left small>fa fa-trash</v-icon>
                      Excluir dados de {{ rotuloTipoPessoa(pessoaFisicaTipo) }}
                    </v-btn>
                    <v-btn
                      color="primary"
                      :loading="salvandoModulo === `${modulo.id}-todas-pessoas`"
                      @click="salvarTodasPessoas(modulo)"
                    >
                      <v-icon left small>fa fa-save</v-icon>
                      Salvar todas as pessoas
                    </v-btn>
                  </div>
                  <v-btn
                    v-if="temErroPessoas"
                    fixed
                    right
                    color="warning"
                    style="bottom: 90px; z-index: 6"
                    @click="irParaProximoErroPessoa(modulo)"
                  >
                    Próximo erro <v-icon x-small right>fa fa-arrow-right</v-icon>
                  </v-btn>
                </template>

                <!-- Módulo 60: alunos (paginado) — 1 card por MATRÍCULA. Um aluno com
                     mais de um vínculo ativo (ex.: Escolarização + AEE) aparece em
                     mais de um card, diferenciados pela turma/tipo de atendimento. -->
                <template v-else-if="isAlunoModulo(modulo)">
                  <div
                    v-if="matriculas.length === 0"
                    class="text-caption grey--text py-4 text-center"
                  >
                    Nenhum aluno encontrado.
                  </div>
                  <template v-else>
                    <div class="pa-4">
                      <v-card
                        v-for="aluno in alunosPaginados"
                        :key="aluno.id"
                        :id="cardElId(60, aluno.id)"
                        outlined
                        class="mb-4"
                        :class="{ 'destaque-pendencia': isDestacado(60, aluno.id) }"
                      >
                        <v-card-subtitle class="font-weight-bold py-2 blue-grey lighten-5">
                          {{ aluno.nomecompleto }}
                          <span
                            v-if="aluno.tipo_de_atendimento || aluno.turma_descricao"
                            class="font-weight-regular text-caption grey--text text--darken-1 ml-2"
                          >
                            ({{
                              [aluno.tipo_de_atendimento, aluno.turma_descricao]
                                .filter(Boolean)
                                .join(" — ")
                            }})
                          </span>
                        </v-card-subtitle>
                        <v-divider />
                        <v-card flat class="pa-4">
                          <template v-if="modulo.campos && modulo.campos.length > 0">
                            <ValidationObserver :key="aluno.id" v-slot="{ handleSubmit }">
                              <v-card
                                v-for="grupo in camposPorGrupo(modulo.campos)"
                                v-if="
                                  grupoTemCamposVisiveis(grupo, respostasAlunos[aluno.id] || {})
                                "
                                :key="grupo.nome"
                                outlined
                                class="mb-4"
                              >
                                <v-card-subtitle
                                  class="font-weight-bold pt-2 mb-3 pb-0 text-uppercase"
                                >
                                  {{ grupo.nome }}
                                </v-card-subtitle>
                                <v-card-text class="pt-1 pb-2">
                                  <v-row>
                                    <v-col
                                      v-for="campo in grupo.campos"
                                      v-if="
                                        campoDependenciaAtendida(
                                          campo,
                                          respostasAlunos[aluno.id] || {}
                                        )
                                      "
                                      :key="campo.id"
                                      cols="12"
                                      sm="6"
                                      class="py-1"
                                    >
                                      <ValidationProvider
                                        :name="campo.nome"
                                        :rules="regrasCampo(campo)"
                                        v-slot="{ errors }"
                                      >
                                        <v-textarea
                                          v-if="campo.tipo === 'textarea'"
                                          v-model="respostasAlunos[aluno.id][campo.id]"
                                          :label="campo.nome"
                                          :placeholder="campo.placeholder || ''"
                                          :error-messages="
                                            errors.length
                                              ? errors
                                              : errosAlunos[aluno.id]?.[campo.id] || []
                                          "
                                          @input="$delete(errosAlunos[aluno.id] || {}, campo.id)"
                                          outlined
                                          dense
                                          rows="3"
                                          auto-grow
                                        />
                                        <v-checkbox
                                          v-else-if="campo.tipo === 'checkbox'"
                                          v-model="respostasAlunos[aluno.id][campo.id]"
                                          :label="campo.nome"
                                          :error-messages="
                                            errors.length
                                              ? errors
                                              : errosAlunos[aluno.id]?.[campo.id] || []
                                          "
                                          @change="$delete(errosAlunos[aluno.id] || {}, campo.id)"
                                          dense
                                          class="mt-0"
                                        />
                                        <v-select
                                          v-else-if="campo.tipo === 'select'"
                                          v-model="respostasAlunos[aluno.id][campo.id]"
                                          :label="campo.nome"
                                          :items="opcoesFormatadas(campo.opcoes)"
                                          :placeholder="campo.placeholder || ''"
                                          :error-messages="
                                            errors.length
                                              ? errors
                                              : errosAlunos[aluno.id]?.[campo.id] || []
                                          "
                                          @change="$delete(errosAlunos[aluno.id] || {}, campo.id)"
                                          outlined
                                          clearable
                                          dense
                                        />
                                        <v-text-field
                                          v-else
                                          v-model="respostasAlunos[aluno.id][campo.id]"
                                          :label="campo.nome"
                                          :placeholder="campo.placeholder || ''"
                                          :type="
                                            campo.tipo === 'number'
                                              ? 'number'
                                              : campo.tipo === 'date'
                                              ? 'date'
                                              : 'text'
                                          "
                                          :error-messages="
                                            errors.length
                                              ? errors
                                              : errosAlunos[aluno.id]?.[campo.id] || []
                                          "
                                          @input="$delete(errosAlunos[aluno.id] || {}, campo.id)"
                                          outlined
                                          dense
                                        />
                                      </ValidationProvider>
                                    </v-col>
                                  </v-row>
                                </v-card-text>
                              </v-card>
                            </ValidationObserver>
                          </template>
                        </v-card>
                      </v-card>
                    </div>
                    <div class="d-flex justify-center pb-4">
                      <v-pagination
                        v-model="alunoPaginaAtual"
                        :length="Math.ceil(matriculas.length / alunosPorPagina)"
                        total-visible="7"
                      />
                    </div>
                  </template>
                  <div class="d-flex justify-end px-4 py-3">
                    <v-btn color="info" outlined class="mr-2" @click="sincronizarModulo(modulo)">
                      <v-icon left small>fa fa-sync</v-icon>
                      Sincronizar com o sistema
                    </v-btn>
                    <v-btn color="error" outlined class="mr-2" @click="excluirDadosModulo(modulo)">
                      <v-icon left small>fa fa-trash</v-icon>
                      Excluir dados
                    </v-btn>
                    <v-btn
                      color="primary"
                      :loading="salvandoModulo === `${modulo.id}-todos-alunos`"
                      @click="salvarTodos(modulo, 'aluno')"
                    >
                      <v-icon left small>fa fa-save</v-icon>
                      Salvar todos os alunos
                    </v-btn>
                  </div>
                </template>

                <!-- Módulo 40: gestores -->
                <template v-else-if="isGestorModulo(modulo)">
                  <div
                    v-if="gestores.length === 0"
                    class="text-caption grey--text py-4 text-center"
                  >
                    Nenhum gestor encontrado.
                  </div>
                  <template v-else>
                    <div class="pa-4">
                      <v-card
                        v-for="gestor in gestoresPaginados"
                        :key="gestor.id"
                        :id="cardElId(40, gestor.id)"
                        outlined
                        class="mb-4"
                        :class="{ 'destaque-pendencia': isDestacado(40, gestor.id) }"
                      >
                        <v-card-subtitle class="font-weight-bold py-2 blue-grey lighten-5">
                          {{ gestor.nome }}
                        </v-card-subtitle>
                        <v-divider />
                        <v-card flat class="pa-4">
                          <template v-if="modulo.campos && modulo.campos.length > 0">
                            <ValidationObserver :key="gestor.id" v-slot="{ handleSubmit }">
                              <v-card
                                v-for="grupo in camposPorGrupo(modulo.campos)"
                                v-if="
                                  grupoTemCamposVisiveis(grupo, respostasGestores[gestor.id] || {})
                                "
                                :key="grupo.nome"
                                outlined
                                class="mb-4"
                              >
                                <v-card-subtitle
                                  class="font-weight-bold pt-2 mb-3 pb-0 text-uppercase"
                                >
                                  {{ grupo.nome }}
                                </v-card-subtitle>
                                <v-card-text class="pt-1 pb-2">
                                  <v-row>
                                    <v-col
                                      v-for="campo in grupo.campos"
                                      v-if="
                                        campoDependenciaAtendida(
                                          campo,
                                          respostasGestores[gestor.id] || {}
                                        )
                                      "
                                      :key="campo.id"
                                      cols="12"
                                      sm="6"
                                      class="py-1"
                                    >
                                      <ValidationProvider
                                        :name="campo.nome"
                                        :rules="regrasCampo(campo)"
                                        v-slot="{ errors }"
                                      >
                                        <v-textarea
                                          v-if="campo.tipo === 'textarea'"
                                          v-model="respostasGestores[gestor.id][campo.id]"
                                          :label="campo.nome"
                                          :placeholder="campo.placeholder || ''"
                                          :error-messages="
                                            errors.length
                                              ? errors
                                              : errosGestores[gestor.id]?.[campo.id] || []
                                          "
                                          @input="$delete(errosGestores[gestor.id] || {}, campo.id)"
                                          outlined
                                          dense
                                          rows="3"
                                          auto-grow
                                        />
                                        <v-checkbox
                                          v-else-if="campo.tipo === 'checkbox'"
                                          v-model="respostasGestores[gestor.id][campo.id]"
                                          :label="campo.nome"
                                          :error-messages="
                                            errors.length
                                              ? errors
                                              : errosGestores[gestor.id]?.[campo.id] || []
                                          "
                                          @change="
                                            $delete(errosGestores[gestor.id] || {}, campo.id)
                                          "
                                          dense
                                          class="mt-0"
                                        />
                                        <v-select
                                          v-else-if="campo.tipo === 'select'"
                                          v-model="respostasGestores[gestor.id][campo.id]"
                                          :label="campo.nome"
                                          :items="opcoesFormatadas(campo.opcoes)"
                                          :placeholder="campo.placeholder || ''"
                                          :error-messages="
                                            errors.length
                                              ? errors
                                              : errosGestores[gestor.id]?.[campo.id] || []
                                          "
                                          @change="
                                            $delete(errosGestores[gestor.id] || {}, campo.id)
                                          "
                                          outlined
                                          clearable
                                          dense
                                        />
                                        <v-text-field
                                          v-else
                                          v-model="respostasGestores[gestor.id][campo.id]"
                                          :label="campo.nome"
                                          :placeholder="campo.placeholder || ''"
                                          :type="
                                            campo.tipo === 'number'
                                              ? 'number'
                                              : campo.tipo === 'date'
                                              ? 'date'
                                              : 'text'
                                          "
                                          :error-messages="
                                            errors.length
                                              ? errors
                                              : errosGestores[gestor.id]?.[campo.id] || []
                                          "
                                          @input="$delete(errosGestores[gestor.id] || {}, campo.id)"
                                          outlined
                                          dense
                                        />
                                      </ValidationProvider>
                                    </v-col>
                                  </v-row>
                                </v-card-text>
                              </v-card>
                            </ValidationObserver>
                          </template>
                        </v-card>
                      </v-card>
                    </div>
                    <div class="d-flex justify-center pb-4">
                      <v-pagination
                        v-model="gestorPaginaAtual"
                        :length="Math.ceil(gestores.length / gestoresPorPagina)"
                        total-visible="7"
                      />
                    </div>
                  </template>
                  <div class="d-flex justify-end px-4 py-3">
                    <v-btn color="info" outlined class="mr-2" @click="sincronizarModulo(modulo)">
                      <v-icon left small>fa fa-sync</v-icon>
                      Sincronizar com o sistema
                    </v-btn>
                    <v-btn color="error" outlined class="mr-2" @click="excluirDadosModulo(modulo)">
                      <v-icon left small>fa fa-trash</v-icon>
                      Excluir dados
                    </v-btn>
                    <v-btn
                      color="primary"
                      :loading="salvandoModulo === `${modulo.id}-todos-gestores`"
                      @click="salvarTodos(modulo, 'gestor')"
                    >
                      <v-icon left small>fa fa-save</v-icon>
                      Salvar todos os gestores
                    </v-btn>
                  </div>
                </template>

                <template v-else-if="isProfessorModulo(modulo)">
                  <div
                    v-if="vinculosProfessor.length === 0"
                    class="text-caption grey--text py-4 text-center"
                  >
                    Nenhum vínculo docente-turma encontrado.
                  </div>
                  <template v-else>
                    <div class="pa-4">
                      <v-card
                        v-for="professor in professoresPaginados"
                        :key="professor.key"
                        :id="cardElId(50, professor.id)"
                        outlined
                        class="mb-4"
                        :class="{ 'destaque-pendencia': isDestacado(50, professor.id) }"
                      >
                        <v-card-subtitle class="font-weight-bold py-2 blue-grey lighten-5">
                          {{ professor.nome }}
                          <span
                            v-if="professor.turma_descricao"
                            class="font-weight-regular text-caption grey--text text--darken-1 ml-2"
                          >
                            ({{ professor.turma_descricao }})
                          </span>
                        </v-card-subtitle>
                        <v-divider />
                        <v-card flat class="pa-4">
                          <template v-if="modulo.campos && modulo.campos.length > 0">
                            <ValidationObserver :key="professor.key" v-slot="{ handleSubmit }">
                              <v-card
                                v-for="grupo in camposPorGrupo(modulo.campos)"
                                v-if="
                                  grupoTemCamposVisiveis(
                                    grupo,
                                    respostasProfessores[professor.key] || {}
                                  )
                                "
                                :key="grupo.nome"
                                outlined
                                class="mb-4"
                              >
                                <v-card-subtitle
                                  class="font-weight-bold pt-2 mb-3 pb-0 text-uppercase"
                                >
                                  {{ grupo.nome }}
                                </v-card-subtitle>
                                <v-card-text class="pt-1 pb-2">
                                  <v-row>
                                    <v-col
                                      v-for="campo in grupo.campos"
                                      v-if="
                                        campoDependenciaAtendida(
                                          campo,
                                          respostasProfessores[professor.key] || {}
                                        )
                                      "
                                      :key="campo.id"
                                      cols="12"
                                      sm="6"
                                      class="py-1"
                                    >
                                      <ValidationProvider
                                        :name="campo.nome"
                                        :rules="regrasCampo(campo)"
                                        v-slot="{ errors }"
                                      >
                                        <v-textarea
                                          v-if="campo.tipo === 'textarea'"
                                          v-model="respostasProfessores[professor.key][campo.id]"
                                          :label="campo.nome"
                                          :placeholder="campo.placeholder || ''"
                                          :error-messages="
                                            errors.length
                                              ? errors
                                              : errosProfessores[professor.key]?.[campo.id] || []
                                          "
                                          @input="
                                            $delete(errosProfessores[professor.key] || {}, campo.id)
                                          "
                                          outlined
                                          dense
                                          rows="3"
                                          auto-grow
                                        />
                                        <v-checkbox
                                          v-else-if="campo.tipo === 'checkbox'"
                                          v-model="respostasProfessores[professor.key][campo.id]"
                                          :label="campo.nome"
                                          :error-messages="
                                            errors.length
                                              ? errors
                                              : errosProfessores[professor.key]?.[campo.id] || []
                                          "
                                          @change="
                                            $delete(errosProfessores[professor.key] || {}, campo.id)
                                          "
                                          dense
                                          class="mt-0"
                                        />
                                        <v-select
                                          v-else-if="campo.tipo === 'select'"
                                          v-model="respostasProfessores[professor.key][campo.id]"
                                          :label="campo.nome"
                                          :items="opcoesFormatadas(campo.opcoes)"
                                          :placeholder="campo.placeholder || ''"
                                          :error-messages="
                                            errors.length
                                              ? errors
                                              : errosProfessores[professor.key]?.[campo.id] || []
                                          "
                                          @change="
                                            $delete(errosProfessores[professor.key] || {}, campo.id)
                                          "
                                          outlined
                                          clearable
                                          dense
                                        />
                                        <v-text-field
                                          v-else
                                          v-model="respostasProfessores[professor.key][campo.id]"
                                          :label="campo.nome"
                                          :placeholder="campo.placeholder || ''"
                                          :type="
                                            campo.tipo === 'number'
                                              ? 'number'
                                              : campo.tipo === 'date'
                                              ? 'date'
                                              : 'text'
                                          "
                                          :error-messages="
                                            errors.length
                                              ? errors
                                              : errosProfessores[professor.key]?.[campo.id] || []
                                          "
                                          @input="
                                            $delete(errosProfessores[professor.key] || {}, campo.id)
                                          "
                                          outlined
                                          dense
                                        />
                                      </ValidationProvider>
                                    </v-col>
                                  </v-row>
                                </v-card-text>
                              </v-card>
                            </ValidationObserver>
                          </template>
                        </v-card>
                      </v-card>
                    </div>
                    <div class="d-flex justify-center pb-4">
                      <v-pagination
                        v-model="professorPaginaAtual"
                        :length="Math.ceil(vinculosProfessor.length / professoresPorPagina)"
                        total-visible="7"
                      />
                    </div>
                  </template>
                  <div class="d-flex justify-end px-4 py-3">
                    <v-btn color="info" outlined class="mr-2" @click="sincronizarModulo(modulo)">
                      <v-icon left small>fa fa-sync</v-icon>
                      Sincronizar com o sistema
                    </v-btn>
                    <v-btn color="error" outlined class="mr-2" @click="excluirDadosModulo(modulo)">
                      <v-icon left small>fa fa-trash</v-icon>
                      Excluir dados
                    </v-btn>
                    <v-btn
                      color="primary"
                      :loading="salvandoModulo === `${modulo.id}-todos-professors`"
                      @click="salvarTodos(modulo, 'professor')"
                    >
                      <v-icon left small>fa fa-save</v-icon>
                      Salvar todos os professores
                    </v-btn>
                  </div>
                </template>

                <!-- Módulos normais -->
                <v-card v-else flat class="pa-4">
                  <template v-if="modulo.campos && modulo.campos.length > 0">
                    <v-checkbox
                      class="mt-0 mb-2"
                      hide-details
                      dense
                      label='Marcar como "Não" os campos Sim/Não obrigatórios ainda em branco'
                      @change="preencherObrigatoriosNao(modulo)"
                    />
                    <ValidationObserver v-slot="{ validate }">
                      <v-card
                        v-for="grupo in camposPorGrupo(modulo.campos)"
                        v-if="grupoTemCamposVisiveis(grupo, respostas)"
                        :key="grupo.nome"
                        outlined
                        class="mb-4"
                      >
                        <v-card-subtitle class="font-weight-bold pt-2 mb-3 pb-0 text-uppercase">
                          {{ grupo.nome }}
                        </v-card-subtitle>
                        <v-card-text class="pt-1 pb-2">
                          <v-row dense>
                            <v-col
                              v-for="campo in grupo.campos"
                              v-if="campoDependenciaAtendida(campo, respostas)"
                              :key="campo.id"
                              :id="campoElId(modulo.codigo, campo.id)"
                              cols="12"
                              sm="6"
                              class="py-1"
                              :class="{
                                'destaque-campo': isCampoDestacado(modulo.codigo, campo.id),
                              }"
                            >
                              <ValidationProvider
                                :name="campo.nome"
                                :rules="regrasCampo(campo)"
                                v-slot="{ errors }"
                              >
                                <v-textarea
                                  v-if="campo.tipo === 'textarea'"
                                  v-model="respostas[campo.id]"
                                  :label="campo.nome"
                                  :placeholder="campo.placeholder || ''"
                                  :error-messages="errors.length ? errors : campo.erros || []"
                                  outlined
                                  dense
                                  rows="3"
                                  auto-grow
                                />
                                <v-checkbox
                                  v-else-if="campo.tipo === 'checkbox'"
                                  v-model="respostas[campo.id]"
                                  :label="campo.nome"
                                  :error-messages="errors.length ? errors : campo.erros || []"
                                  dense
                                  class="mt-0"
                                />
                                <v-select
                                  v-else-if="campo.tipo === 'select'"
                                  v-model="respostas[campo.id]"
                                  :label="campo.nome"
                                  :items="opcoesFormatadas(campo.opcoes)"
                                  :placeholder="campo.placeholder || ''"
                                  :error-messages="errors.length ? errors : campo.erros || []"
                                  outlined
                                  clearable
                                  dense
                                />
                                <v-text-field
                                  v-else
                                  v-model="respostas[campo.id]"
                                  :label="campo.nome"
                                  :placeholder="campo.placeholder || ''"
                                  :type="
                                    campo.tipo === 'number'
                                      ? 'number'
                                      : campo.tipo === 'date'
                                      ? 'date'
                                      : 'text'
                                  "
                                  :error-messages="errors.length ? errors : campo.erros || []"
                                  outlined
                                  dense
                                />
                              </ValidationProvider>
                            </v-col>
                          </v-row>
                        </v-card-text>
                      </v-card>
                      <div class="d-flex justify-end mt-2">
                        <v-btn
                          color="info"
                          outlined
                          class="mr-2"
                          @click="sincronizarModulo(modulo)"
                        >
                          <v-icon left small>fa fa-sync</v-icon>
                          Sincronizar com o sistema
                        </v-btn>
                        <v-btn
                          color="error"
                          outlined
                          class="mr-2"
                          @click="excluirDadosModulo(modulo)"
                        >
                          <v-icon left small>fa fa-trash</v-icon>
                          Excluir dados
                        </v-btn>
                        <v-btn
                          color="primary"
                          :loading="salvandoModulo === modulo.id"
                          @click="salvarComValidacao(validate, () => salvarRespostas(modulo))"
                        >
                          <v-icon left small>fa fa-save</v-icon>
                          Salvar
                        </v-btn>
                      </div>
                    </ValidationObserver>
                  </template>
                  <div v-else class="text-caption grey--text py-2 text-center">
                    Nenhum campo configurado neste módulo.
                  </div>
                </v-card>
              </v-tab-item>
            </v-tabs-items>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <!-- Progresso do "salvar todos" (pessoa física, gestor, docente) -->
    <v-dialog v-model="dialogProgressoSalvamento" max-width="380" persistent>
      <v-card class="pa-6 text-center">
        <div class="text-subtitle-1 font-weight-bold mb-4">{{ progressoSalvamento.titulo }}</div>
        <v-progress-linear
          :value="percentualProgressoSalvamento"
          height="10"
          rounded
          color="primary"
          class="mb-2"
        />
        <div class="text-caption grey--text">
          {{ progressoSalvamento.atual }} de {{ progressoSalvamento.total }} salvos
        </div>
      </v-card>
    </v-dialog>

    <!-- Painel de pendências (erros clicáveis → levam direto ao campo) -->
    <v-dialog v-model="dialogPendencias" max-width="760" scrollable>
      <v-card v-if="pendencias">
        <v-card-title class="text-h6">
          <v-icon color="error" class="mr-2">error_outline</v-icon>
          Pendências do censo
        </v-card-title>
        <v-card-subtitle class="pt-2">
          {{ pendencias.etapaNome }} — {{ pendencias.totalErros }} pendência(s). Clique em uma
          pendência para ir direto ao campo.
        </v-card-subtitle>
        <v-divider />
        <v-card-text style="max-height: 60vh">
          <div v-for="grupo in pendencias.regrasEtapa" :key="`regra-${grupo.codigo}`" class="mb-4">
            <div class="text-subtitle-2 font-weight-bold mb-2">
              R{{ grupo.codigo }} — {{ grupo.nome }} (regras da etapa)
            </div>
            <v-alert
              v-for="(regra, i) in grupo.erros"
              :key="`r-${grupo.codigo}-${i}`"
              type="error"
              dense
              text
              class="mb-2 text-body-2 pendencia-clicavel"
              @click="irParaErroRegra(regra)"
            >
              {{ regra.msg || regra }}
            </v-alert>
          </div>

          <div v-for="modulo in pendencias.modulos" :key="modulo.codigo" class="mb-4">
            <div class="text-subtitle-2 font-weight-bold mb-1">
              R{{ modulo.codigo }} — {{ modulo.nome }}
            </div>
            <v-expansion-panels accordion flat>
              <v-expansion-panel
                v-for="(ent, j) in modulo.entidades"
                :key="`${modulo.codigo}-${j}`"
              >
                <v-expansion-panel-header class="py-1">
                  <span class="text-body-2">
                    {{ ent.nome }}
                    <v-chip x-small color="error" outlined class="ml-2">
                      {{ ent.erros.length }}
                    </v-chip>
                  </span>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <ul class="text-body-2 grey--text text--darken-2 pl-4">
                    <li
                      v-for="(erro, k) in ent.erros"
                      :key="k"
                      class="mb-1 pendencia-clicavel"
                      @click="irParaErroEntidade(modulo.codigo, ent, erro)"
                    >
                      {{ erro }}
                    </li>
                  </ul>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            outlined
            :loading="gerandoRelatorio"
            @click="gerarRelatorioPendencias"
          >
            <v-icon left small>fa fa-file-pdf</v-icon>
            Gerar relatório
          </v-btn>
          <v-btn text @click="dialogPendencias = false">Fechar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </main-template>
</template>

<script>
export default {
  data() {
    return {
      loading: true,
      censos: [],
      censoSelecionado: null,
      etapaSelecionada: null,
      etapaDetalhada: null,
      loadingEtapa: false,
      moduloAtivo: 0,
      respostas: {},
      errosTurma: {},
      errosPessoas: {},
      errosAlunos: {},
      errosGestores: {},
      salvandoModulo: null,
      dialogProgressoSalvamento: false,
      progressoSalvamento: { titulo: "", atual: 0, total: 0 },
      gerandoArquivo: false,
      dialogPendencias: false,
      pendencias: null,
      gerandoRelatorio: false,
      destaque: null,
      destaqueCampo: null,
      turmas: [],
      turmaAtiva: 0,
      // Módulo Pessoa Física (30, subtipo aluno): 1 registro por aluno.
      alunos: [],
      // Módulo Aluno (60): 1 registro por MATRÍCULA — um aluno com mais de um
      // vínculo ativo (Escolarização + AEE) aparece mais de uma vez aqui.
      matriculas: [],
      alunoPaginaAtual: 1,
      alunosPorPagina: 5,
      respostasAlunos: {},
      gestores: [],
      gestorPaginaAtual: 1,
      gestoresPorPagina: 5,
      respostasGestores: {},
      errosProfessores: {},
      professorPaginaAtual: 1,
      professoresPorPagina: 5,
      respostasProfessores: {},
      // R30 (Pessoa Física): professores como pessoas distintas (1 por pessoa).
      professores: [],
      // R50 (Docente): vínculos (professor + turma). Um professor em N turmas
      // aparece N vezes; cada item tem `key` = "{professorId}_{turmaId}".
      vinculosProfessor: [],
      pessoaFisicaTipo: "professor",
      pessoaPaginaAtual: 1,
      pessoasPorPagina: 5,
      subTabsPessoas: {},
      respostasPessoas: {},
      // Índice (na lista combinada professor+aluno+gestor) da última pessoa para a
      // qual navegamos por causa de um erro — âncora do botão "Próximo erro".
      pessoaIndiceErroAtual: -1,
    };
  },

  computed: {
    pessoasAtuais() {
      if (this.pessoaFisicaTipo === "professor") return this.professores;
      if (this.pessoaFisicaTipo === "aluno") return this.alunos;
      if (this.pessoaFisicaTipo === "gestor") return this.gestores;
      return [];
    },
    pessoasPaginadas() {
      const inicio = (this.pessoaPaginaAtual - 1) * this.pessoasPorPagina;
      return this.pessoasAtuais.slice(inicio, inicio + this.pessoasPorPagina);
    },
    alunosPaginados() {
      const inicio = (this.alunoPaginaAtual - 1) * this.alunosPorPagina;
      return this.matriculas.slice(inicio, inicio + this.alunosPorPagina);
    },
    gestoresPaginados() {
      const inicio = (this.gestorPaginaAtual - 1) * this.gestoresPorPagina;
      return this.gestores.slice(inicio, inicio + this.gestoresPorPagina);
    },
    professoresPaginados() {
      const inicio = (this.professorPaginaAtual - 1) * this.professoresPorPagina;
      // R50 é por vínculo (professor + turma).
      return this.vinculosProfessor.slice(inicio, inicio + this.professoresPorPagina);
    },
    temErroPessoas() {
      return Object.keys(this.errosPessoas).length > 0;
    },
    percentualProgressoSalvamento() {
      if (!this.progressoSalvamento.total) return 0;
      return (this.progressoSalvamento.atual / this.progressoSalvamento.total) * 100;
    },
  },

  async created() {
    await this.buscarCensos();
  },

  methods: {
    campoDependenciaAtendida(campo, respostas) {
      if (!campo.dependencias || campo.dependencias.length === 0) return true;
      return campo.dependencias.every((dep) => {
        const valorAtual = String(respostas[dep.campo_gatilho_id] ?? "");

        switch (dep.operador) {
          case "eq":
            return valorAtual === String(dep.valor);
          case "neq":
            return valorAtual !== String(dep.valor);
          case "in":
            return dep.valor
              ?.split(",")
              .map((v) => v.trim())
              .includes(valorAtual);
          case "filled":
            return !!valorAtual;
          default:
            return true;
        }
      });
    },

    grupoTemCamposVisiveis(grupo, respostas) {
      return grupo.campos.some((campo) => this.campoDependenciaAtendida(campo, respostas));
    },

    isCampoSimNaoObrigatorio(campo) {
      if (campo?.tipo !== "select") return false;
      const exigido = (campo?.validacoes || []).some((v) =>
        ["required", "conditional"].includes(v?.regra?.code)
      );
      if (!exigido) return false;
      const valores = this.opcoesFormatadas(campo.opcoes).map((o) => o.value);
      return valores.length === 2 && valores.includes("0") && valores.includes("1");
    },

    // Preenche com "Não" ('0') os campos Sim/Não obrigatórios, visíveis e ainda em
    // branco do módulo. Não sobrescreve o que já veio preenchido.
    preencherObrigatoriosNao(modulo) {
      (modulo.campos || []).forEach((campo) => {
        if (!this.isCampoSimNaoObrigatorio(campo)) return;
        if (!this.campoDependenciaAtendida(campo, this.respostas)) return;

        const atual = this.respostas[campo.id];
        const preenchido = atual !== null && atual !== undefined && atual !== "";
        if (preenchido) return;

        this.$set(this.respostas, campo.id, "0");
      });
    },

    voltarTelaAnterior() {
      this.$router.go(-1);
    },

    async gerarArquivo() {
      this.gerandoArquivo = true;
      try {
        // Valida a etapa inteira antes de gerar. Se houver pendências, abre o
        // painel (com erros clicáveis) e NÃO gera.
        const relatorio = await this.$services.censoService.validarEtapa(this.etapaSelecionada.id);
        if (!relatorio.valido) {
          this.pendencias = {
            ...relatorio,
            etapaId: this.etapaSelecionada.id,
            etapaNome: this.etapaSelecionada.descricao || `Etapa ${this.etapaSelecionada.id}`,
          };
          this.dialogPendencias = true;
          this.$toast.danger(
            `Existem ${relatorio.totalErros} pendência(s) — corrija antes de gerar.`
          );
          return;
        }
        await this.$services.censoService.gerarArquivoCenso(this.etapaSelecionada.id);
        this.$toast.success("Arquivo gerado com sucesso.");
      } catch {
        this.$toast.danger("Erro ao gerar o arquivo.");
      } finally {
        this.gerandoArquivo = false;
      }
    },

    async gerarRelatorioPendencias() {
      if (!this.pendencias) return;
      this.gerandoRelatorio = true;
      try {
        await this.$services.censoService.relatorioPendencias(this.pendencias.etapaId);
      } catch {
        this.$toast.danger("Erro ao gerar o relatório.");
      } finally {
        this.gerandoRelatorio = false;
      }
    },

    // ── Navegação a partir das pendências ─────────────────────────────────
    // Mapeia o rótulo do erro (Aluno/Docente/Gestor/Turma) para o tipo interno.
    tipoEntidadePorRotulo(rotulo) {
      const m = {
        aluno: "aluno",
        docente: "professor",
        professor: "professor",
        gestor: "gestor",
        turma: "turma",
      };
      return m[(rotulo || "").toLowerCase()] || null;
    },

    // Extrai { codigo, ordem } de uma mensagem que começa com "R{cod}/{ordem}".
    parseCodigoOrdem(msg) {
      const m = /R(\d+)(?:\/(\d+))?/.exec(msg || "");
      if (!m) return { codigo: null, ordem: null };
      return { codigo: Number(m[1]), ordem: m[2] != null ? Number(m[2]) : null };
    },

    // Resolve o id da entidade pelo nome (as "Regras da etapa" só trazem o nome).
    idPorNome(tipo, nome) {
      if (!tipo || !nome) return null;
      const alvo = String(nome).trim();
      if (tipo === "aluno")
        return this.alunos.find((a) => (a.nomecompleto || "").trim() === alvo)?.id ?? null;
      if (tipo === "professor")
        return (
          this.professores.find((p) => (p.nome || p.nomecompleto || "").trim() === alvo)?.id ?? null
        );
      if (tipo === "gestor")
        return this.gestores.find((g) => (g.nome || g.name || "").trim() === alvo)?.id ?? null;
      if (tipo === "turma")
        return this.turmas.find((t) => (t.descricao || "").trim() === alvo)?.id ?? null;
      return null;
    },

    // Erro da seção "Regras da etapa". O backend já manda entidadeTipo/entidadeId
    // (preciso); só cai no match por nome se vier um formato antigo (string).
    irParaErroRegra(regra) {
      const msg = typeof regra === "string" ? regra : regra.msg;
      const { codigo, ordem } = this.parseCodigoOrdem(msg);
      if (codigo === null) return;

      let tipo = typeof regra === "object" ? regra.entidadeTipo || null : null;
      let id = typeof regra === "object" && regra.entidadeId != null ? regra.entidadeId : null;

      if (id == null) {
        const ent = /(Aluno|Docente|Professor|Gestor|Turma|turma)\s+"([^"]+)"/.exec(msg);
        if (ent) {
          tipo = this.tipoEntidadePorRotulo(ent[1]);
          id = this.idPorNome(tipo, ent[2]);
        }
      }
      this.irParaErro({ codigo, ordem, tipo, id });
    },

    // Erro de uma entidade específica (já vem com tipo/id do backend).
    irParaErroEntidade(moduloCodigo, ent, msg) {
      const { ordem } = this.parseCodigoOrdem(msg);
      const tipo = ent.tipo === "escola" ? null : ent.tipo;
      this.irParaErro({ codigo: Number(moduloCodigo), ordem, tipo, id: ent.id });
    },

    // Navega abas/sub-abas/paginação até o campo do erro e fecha o painel.
    async irParaErro({ codigo, ordem, tipo, id }) {
      const modulos = this.etapaDetalhada?.modulos || [];
      const idxModulo = modulos.findIndex((m) => Number(m.codigo) === Number(codigo));
      if (idxModulo === -1) return;
      this.moduloAtivo = idxModulo;
      this.dialogPendencias = false;
      await this.$nextTick();

      if (codigo === 30 && tipo && id != null) {
        // Pessoa física: tipo (radio) + página + sub-aba (faixa de ordem).
        this.pessoaFisicaTipo = tipo;
        const lista =
          tipo === "professor" ? this.professores : tipo === "aluno" ? this.alunos : this.gestores;
        const idx = lista.findIndex((p) => p.id === id);
        if (idx !== -1) this.pessoaPaginaAtual = Math.floor(idx / this.pessoasPorPagina) + 1;
        if (ordem != null) {
          const faixa = this.indiceFaixaCampo(ordem);
          if (faixa !== -1) this.$set(this.subTabsPessoas, `${tipo}_${id}`, faixa);
        }
      } else if (codigo === 20 && id != null) {
        const idx = this.turmas.findIndex((t) => t.id === id);
        if (idx !== -1) this.turmaAtiva = idx;
      } else if (codigo === 40 && id != null) {
        const idx = this.gestores.findIndex((g) => g.id === id);
        if (idx !== -1) this.gestorPaginaAtual = Math.floor(idx / this.gestoresPorPagina) + 1;
      } else if (codigo === 50 && id != null) {
        const idx = this.professores.findIndex((p) => p.id === id);
        if (idx !== -1) this.professorPaginaAtual = Math.floor(idx / this.professoresPorPagina) + 1;
      } else if (codigo === 60 && id != null) {
        // Módulo 60 é indexado por matrícula (id vem de ValidarEtapaCensoAction
        // como matricula_id, não aluno_id).
        const idx = this.matriculas.findIndex((m) => m.id === id);
        if (idx !== -1) this.alunoPaginaAtual = Math.floor(idx / this.alunosPorPagina) + 1;
      }
      // R00/R10 (escola): sem card/lista — destaca o campo direto pela ordem.
      this.$nextTick(() => {
        if (codigo === 0 || codigo === 10) {
          this.destacarCampo(codigo, ordem);
        } else {
          this.destacarEntidade(codigo, id);
        }
      });
    },

    // ── Destaque transitório do card (entidades paginadas) ────────────────
    cardElId(codigo, id) {
      return `censo-card-${codigo}-${id}`;
    },

    isDestacado(codigo, id) {
      return !!this.destaque && this.destaque.codigo === Number(codigo) && this.destaque.id === id;
    },

    destacarEntidade(codigo, id) {
      if (id == null) return; // sem entidade → nada a destacar.
      this.destaque = { codigo: Number(codigo), id };
      this.$nextTick(() => {
        const el = document.getElementById(this.cardElId(codigo, id));
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      });
      clearTimeout(this._destaqueTimer);
      this._destaqueTimer = setTimeout(() => {
        this.destaque = null;
      }, 2800);
    },

    // ── Destaque transitório do campo (módulo escola R00/R10) ─────────────
    campoElId(codigo, campoId) {
      return `censo-campo-${Number(codigo)}-${campoId}`;
    },

    isCampoDestacado(codigo, campoId) {
      return (
        !!this.destaqueCampo &&
        this.destaqueCampo.codigo === Number(codigo) &&
        this.destaqueCampo.campoId === campoId
      );
    },

    // Resolve o campo pela ordem (R10/182 → ordem 182) dentro do módulo, rola
    // até ele e aplica a borda por ~2,8s.
    destacarCampo(codigo, ordem) {
      if (ordem == null) return;
      const modulo = (this.etapaDetalhada?.modulos || []).find(
        (m) => Number(m.codigo) === Number(codigo)
      );
      const campo = (modulo?.campos || []).find((c) => Number(c.ordem) === Number(ordem));
      if (!campo) return;
      this.destaqueCampo = { codigo: Number(codigo), campoId: campo.id };
      this.$nextTick(() => {
        const el = document.getElementById(this.campoElId(codigo, campo.id));
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      });
      clearTimeout(this._destaqueCampoTimer);
      this._destaqueCampoTimer = setTimeout(() => {
        this.destaqueCampo = null;
      }, 2800);
    },

    listaPessoasCombinada() {
      return [
        { tipo: "professor", lista: this.professores },
        { tipo: "aluno", lista: this.alunos },
        { tipo: "gestor", lista: this.gestores },
      ].flatMap(({ tipo, lista }) => lista.map((pessoa) => ({ tipo, pessoa })));
    },

    // Navega até a pessoa (aba, página, sub-tab do campo com erro), usado tanto pelo
    // salvamento em lote quanto pelo botão "Próximo erro".
    navegarParaPessoa(modulo, tipo, pessoa) {
      this.pessoaFisicaTipo = tipo;
      const lista =
        tipo === "professor" ? this.professores : tipo === "aluno" ? this.alunos : this.gestores;
      const indice = lista.indexOf(pessoa);
      this.pessoaPaginaAtual = Math.floor(indice / this.pessoasPorPagina) + 1;
      this.pessoaIndiceErroAtual = this.listaPessoasCombinada().findIndex(
        (e) => e.tipo === tipo && e.pessoa.id === pessoa.id
      );

      const mapaErro = this.errosPessoas[`${tipo}_${pessoa.id}`];
      const campoErroId = mapaErro ? Object.keys(mapaErro)[0] : null;
      const campoErro = campoErroId
        ? modulo.campos.find((c) => c.id === parseInt(campoErroId))
        : null;
      const idxFaixa = campoErro ? this.indiceFaixaCampo(campoErro.ordem) : -1;
      if (idxFaixa !== -1) {
        this.$set(this.subTabsPessoas, `${tipo}_${pessoa.id}`, idxFaixa);
      }

      this.destacarEntidade(30, pessoa.id);
    },

    // Pula para o próximo registro com erro (com volta ao início se chegar ao fim),
    // sem precisar reenviar nada — usa os erros já coletados em `errosPessoas`.
    irParaProximoErroPessoa(modulo) {
      const combinada = this.listaPessoasCombinada();
      const comErro = combinada.filter(
        ({ tipo, pessoa }) => this.errosPessoas[`${tipo}_${pessoa.id}`]
      );
      if (!comErro.length) {
        this.$toast.success("Nenhum erro pendente.");
        return;
      }

      const indiceDe = ({ tipo, pessoa }) =>
        combinada.findIndex((e) => e.tipo === tipo && e.pessoa.id === pessoa.id);

      const proximo = comErro.find((e) => indiceDe(e) > this.pessoaIndiceErroAtual) || comErro[0];
      this.navegarParaPessoa(modulo, proximo.tipo, proximo.pessoa);
    },

    async salvarTodasPessoas(modulo) {
      const tipos = [
        { tipo: "professor", lista: this.professores, chave: "pessoa_id", pessoaTipo: "professor" },
        { tipo: "aluno", lista: this.alunos, chave: "pessoa_id", pessoaTipo: "aluno" },
        { tipo: "gestor", lista: this.gestores, chave: "pessoa_id", pessoaTipo: "gestor" },
      ];

      const novosErros = {};

      for (const { tipo, lista } of tipos) {
        for (const pessoa of lista) {
          const chaveResposta = `${tipo}_${pessoa.id}`;
          const respostasPessoa = this.respostasPessoas[chaveResposta] || {};
          for (const campo of modulo.campos || []) {
            if (!this.campoDependenciaAtendida(campo, respostasPessoa)) continue;
            const errosCampo = this.errosLocaisCampo(campo, respostasPessoa[campo.id], tipo);
            if (errosCampo.length) {
              if (!novosErros[chaveResposta]) novosErros[chaveResposta] = {};
              novosErros[chaveResposta][campo.id] = errosCampo;
            }
          }
        }
      }

      if (Object.keys(novosErros).length > 0) {
        this.errosPessoas = novosErros;

        for (const { tipo, lista } of [...tipos].reverse()) {
          for (const pessoa of [...lista].reverse()) {
            const chaveResposta = `${tipo}_${pessoa.id}`;
            if (novosErros[chaveResposta]) {
              this.$toast.danger(`Corrija os campos destacados de "${this.pessoaNome(pessoa)}".`);
            }
          }
        }

        // navega até a primeira pessoa com erro
        for (const { tipo, lista } of tipos) {
          const pessoaComErro = lista.find((p) => novosErros[`${tipo}_${p.id}`]);
          if (pessoaComErro) {
            this.navegarParaPessoa(modulo, tipo, pessoaComErro);
            break;
          }
        }

        return;
      }

      this.errosPessoas = {};
      this.pessoaIndiceErroAtual = -1;

      this.salvandoModulo = `${modulo.id}-todas-pessoas`;
      const totalPessoas = tipos.reduce((soma, { lista }) => soma + lista.length, 0);
      this.progressoSalvamento = { titulo: "Salvando pessoas...", atual: 0, total: totalPessoas };
      this.dialogProgressoSalvamento = true;
      try {
        const errosBackend = {};
        let temFalha = false;
        let navegou = false;

        // Navega para o primeiro erro assim que ele chegar, sem esperar o lote inteiro
        // terminar; os demais continuam salvando em segundo plano e seus erros vão se
        // somando a `errosPessoas` conforme chegam.
        const tarefas = tipos.flatMap(({ tipo, lista, chave, pessoaTipo }) =>
          lista.map((pessoa) => {
            const respostas = this.respostasPessoas[`${tipo}_${pessoa.id}`] || {};
            const payload = {
              [chave]: pessoa.id,
              pessoa_tipo: pessoaTipo,
              respostas: this.filtrarRespostas(modulo, respostas),
            };
            return this.$services.censoService
              .salvarModulo(modulo.id, payload)
              .catch((err) => {
                temFalha = true;
                const nome = this.pessoaNome(pessoa);
                const erros = this.extrairErros(err);
                if (!erros) {
                  this.$toast.danger(`Erro ao salvar "${nome}".`);
                  return;
                }
                const mapa = this.mapearErrosBackend(modulo, erros);
                if (Object.keys(mapa).length) {
                  errosBackend[`${tipo}_${pessoa.id}`] = mapa;
                  this.errosPessoas = { ...errosBackend };
                  if (!navegou) {
                    navegou = true;
                    this.navegarParaPessoa(modulo, tipo, pessoa);
                  }
                }
                Object.values(erros)
                  .flat()
                  .forEach((msg) => this.$toast.danger(`${nome} — ${msg}`));
              })
              .finally(() => {
                this.progressoSalvamento.atual++;
              });
          })
        );

        await Promise.all(tarefas);

        if (!temFalha) {
          this.$toast.success("Salvo com sucesso.");
          this.errosPessoas = {};
        }
      } finally {
        this.salvandoModulo = null;
        this.dialogProgressoSalvamento = false;
      }
    },

    async salvarTodos(modulo, tipo) {
      const mapa = {
        // Módulo 60 é por matrícula (vínculo), não por aluno — ver `matriculas`.
        aluno: {
          lista: this.matriculas,
          respostas: this.respostasAlunos,
          chave: "matricula_id",
          erros: "errosAlunos",
          pagina: "alunoPaginaAtual",
          porPagina: this.alunosPorPagina,
          pessoaTipo: null,
          porVinculo: false,
        },
        gestor: {
          lista: this.gestores,
          respostas: this.respostasGestores,
          chave: "gestor_id",
          erros: "errosGestores",
          pagina: "gestorPaginaAtual",
          porPagina: this.gestoresPorPagina,
          pessoaTipo: null,
          porVinculo: false,
        },
        // R50 é por VÍNCULO (professor + turma): a lista são os vínculos, a resposta
        // é chaveada por `key` e o payload envia pessoa_id (professor) + turma_id.
        professor: {
          lista: this.vinculosProfessor,
          respostas: this.respostasProfessores,
          chave: "pessoa_id",
          erros: "errosProfessores",
          pagina: "professorPaginaAtual",
          porPagina: this.professoresPorPagina,
          pessoaTipo: "professor",
          porVinculo: true,
        },
      };
      const {
        lista,
        respostas,
        chave,
        erros: chaveErros,
        pagina: chavePagina,
        porPagina,
        pessoaTipo,
        porVinculo,
      } = mapa[tipo];

      // Chave da resposta/erro por entidade: no R50 é o `key` do vínculo; nos demais, o id.
      const chaveResp = (entidade) => (porVinculo ? entidade.key : entidade.id);

      const novosErros = {};
      for (const entidade of lista) {
        const respostasEntidade = respostas[chaveResp(entidade)] || {};
        for (const campo of modulo.campos || []) {
          if (!this.campoDependenciaAtendida(campo, respostasEntidade)) continue;
          const errosCampo = this.errosLocaisCampo(campo, respostasEntidade[campo.id]);
          if (errosCampo.length) {
            if (!novosErros[chaveResp(entidade)]) novosErros[chaveResp(entidade)] = {};
            novosErros[chaveResp(entidade)][campo.id] = errosCampo;
          }
        }
      }

      if (Object.keys(novosErros).length > 0) {
        this[chaveErros] = novosErros;
        for (const entidade of [...lista].reverse()) {
          if (novosErros[chaveResp(entidade)]) {
            const nome = entidade.nomecompleto || entidade.nome || "—";
            this.$toast.danger(`Corrija os campos destacados de "${nome}".`);
          }
        }
        const indiceComErro = lista.findIndex((e) => novosErros[chaveResp(e)]);
        if (indiceComErro !== -1) {
          this[chavePagina] = Math.floor(indiceComErro / porPagina) + 1;
        }
        return;
      }

      this[chaveErros] = {};

      const titulos = {
        aluno: "Salvando alunos...",
        gestor: "Salvando gestores...",
        professor: "Salvando docentes...",
      };
      this.progressoSalvamento = { titulo: titulos[tipo], atual: 0, total: lista.length };
      this.dialogProgressoSalvamento = true;

      this.salvandoModulo = `${modulo.id}-todos-${tipo}s`;
      try {
        const errosBackend = {};
        let temFalha = false;
        let navegou = false;

        // Navega para o primeiro erro assim que ele chegar, sem esperar o lote inteiro
        // terminar; os demais continuam salvando em segundo plano.
        const tarefas = lista.map((entidade) => {
          const payload = {
            [chave]: entidade.id,
            ...(pessoaTipo ? { pessoa_tipo: pessoaTipo } : {}),
            ...(porVinculo ? { turma_id: entidade.turma_id } : {}),
            respostas: this.filtrarRespostas(modulo, respostas[chaveResp(entidade)] || {}),
          };
          return this.$services.censoService
            .salvarModulo(modulo.id, payload)
            .catch((err) => {
              temFalha = true;
              const nome = entidade.nomecompleto || entidade.nome || "—";
              const erros = this.extrairErros(err);
              if (!erros) {
                this.$toast.danger(`Erro ao salvar "${nome}".`);
                return;
              }
              const mapa = this.mapearErrosBackend(modulo, erros);
              if (Object.keys(mapa).length) {
                errosBackend[chaveResp(entidade)] = mapa;
                this[chaveErros] = { ...errosBackend };
                if (!navegou) {
                  navegou = true;
                  const idx = lista.indexOf(entidade);
                  if (idx !== -1) this[chavePagina] = Math.floor(idx / porPagina) + 1;
                }
              }
              Object.values(erros)
                .flat()
                .forEach((msg) => this.$toast.danger(`${nome} — ${msg}`));
            })
            .finally(() => {
              this.progressoSalvamento.atual++;
            });
        });

        await Promise.all(tarefas);

        if (!temFalha) {
          this.$toast.success("Salvo com sucesso.");
          this[chaveErros] = {};
        }
      } finally {
        this.salvandoModulo = null;
        this.dialogProgressoSalvamento = false;
      }
    },

    limparErroTurma(turmaId, campoId) {
      if (this.errosTurma[turmaId]?.[campoId]) {
        this.$delete(this.errosTurma[turmaId], campoId);
      }
    },

    limparErroPessoa(tipo, pessoaId, campoId) {
      const chave = `${tipo}_${pessoaId}`;
      if (this.errosPessoas[chave]?.[campoId]) {
        this.$delete(this.errosPessoas[chave], campoId);
      }
    },

    async salvarTodasTurmas(modulo) {
      const novosErros = {};

      for (const turma of this.turmas) {
        const respostasTurma = this.respostas[turma.id] || {};
        for (const campo of modulo.campos || []) {
          if (!this.campoDependenciaAtendida(campo, respostasTurma)) continue;
          const errosCampo = this.errosLocaisCampo(campo, respostasTurma[campo.id]);
          if (errosCampo.length) {
            if (!novosErros[turma.id]) novosErros[turma.id] = {};
            novosErros[turma.id][campo.id] = errosCampo;
          }
        }
      }

      if (Object.keys(novosErros).length > 0) {
        this.errosTurma = novosErros;

        for (const turma of [...this.turmas].reverse()) {
          if (novosErros[turma.id]) {
            this.$toast.danger(`Corrija os campos destacados da turma "${turma.descricao}".`);
          }
        }

        const primeiraComErro = this.turmas.findIndex((t) => novosErros[t.id]);
        if (primeiraComErro !== -1) {
          this.turmaAtiva = primeiraComErro;
        }

        return;
      }

      this.errosTurma = {};

      this.salvandoModulo = `${modulo.id}-todas-turmas`;
      try {
        const errosBackend = {};
        let temFalha = false;
        let navegou = false;

        // Navega para a primeira turma com erro assim que ele chegar, sem esperar o
        // lote inteiro terminar; as demais continuam salvando em segundo plano.
        const tarefas = this.turmas.map((turma) => {
          const payload = {
            turma_id: turma.id,
            respostas: this.filtrarRespostas(modulo, this.respostas[turma.id] || {}),
          };
          return this.$services.censoService.salvarModulo(modulo.id, payload).catch((err) => {
            temFalha = true;
            const erros = this.extrairErros(err);
            if (!erros) {
              this.$toast.danger(`Erro ao salvar a turma "${turma.descricao}".`);
              return;
            }
            const mapa = this.mapearErrosBackend(modulo, erros);
            if (Object.keys(mapa).length) {
              errosBackend[turma.id] = mapa;
              this.errosTurma = { ...errosBackend };
              if (!navegou) {
                navegou = true;
                const idx = this.turmas.indexOf(turma);
                if (idx !== -1) this.turmaAtiva = idx;
              }
            }
            Object.values(erros)
              .flat()
              .forEach((msg) => this.$toast.danger(`Turma "${turma.descricao}" — ${msg}`));
          });
        });

        await Promise.all(tarefas);

        if (!temFalha) {
          this.$toast.success("Todas as turmas salvas com sucesso.");
          this.errosTurma = {};
        }
      } finally {
        this.salvandoModulo = null;
      }
    },

    exibirErros(e) {
      const erros = e?.errors ?? e?.response?.data?.errors;
      if (erros) {
        // Um toast por erro — a mensagem já contém nome e código do campo.
        Object.values(erros)
          .flat()
          .forEach((msg) => this.$toast.danger(msg));
      } else {
        this.$toast.danger("Erro ao salvar.");
      }
    },

    // Mapeia erros do backend (chaveados por nome do campo) para o estado de erro
    // por id ({ [campo.id]: [msgs] }), usado para destacar o campo em vermelho.
    mapearErrosBackend(modulo, erros) {
      const mapa = {};
      (modulo.campos || []).forEach((campo) => {
        if (erros[campo.nome]) mapa[campo.id] = erros[campo.nome];
      });
      return mapa;
    },

    extrairErros(e) {
      return e?.errors ?? e?.response?.data?.errors;
    },

    opcoesFormatadas(opcoes) {
      return (opcoes || []).map((op) => {
        const idx = op.indexOf("-");

        if (idx === -1) {
          return {
            text: op,
            value: op,
          };
        }

        return {
          text: op.slice(idx + 1).trim(),
          value: op.slice(0, idx).trim(),
        };
      });
    },

    filtrarRespostas(modulo, respostas) {
      const filtrado = {};
      for (const campo of modulo.campos || []) {
        filtrado[campo.id] = this.campoDependenciaAtendida(campo, respostas)
          ? respostas[campo.id] ?? null
          : null;
      }
      return filtrado;
    },

    isRequired(campo, tipo = null) {
      // `conditional` conta como obrigatório: o campo só é renderizado quando
      // campoDependenciaAtendida() é true, e nesse caso o backend exige (mesma
      // regra do ValidarRespostasService). Sem isto, o "Salvar todas" não barra
      // nem navega até o campo conditional vazio (ex: R30/56 escolaridade).
      const v = campo?.validacoes || [];
      if (v.some((x) => x?.regra?.code === "required")) return true;
      if (v.some((x) => x?.regra?.code === "conditional")) {
        // Exceção: R30/56 (escolaridade) não se aplica a aluno — espelha o skip
        // do backend (ValidarRespostasService).
        return !(tipo === "aluno" && Number(campo?.ordem) === 56);
      }
      return false;
    },

    // Valida um campo localmente contra TODAS as regras (espelha o backend), para
    // bloquear o envio antes da request — não só obrigatório. Regras de grupo
    // (pelo_menos_um/exclusivo/unico) ficam a cargo do backend. Retorna mensagens.
    errosLocaisCampo(campo, valor, tipo = null) {
      const erros = [];
      const v = valor === null || valor === undefined ? "" : String(valor);
      const vazio = v === "";

      (campo?.validacoes || []).forEach((val) => {
        const code = val?.regra?.code;
        const p = val?.valor;
        switch (code) {
          case "required":
            if (vazio) erros.push("Campo obrigatório.");
            break;
          case "conditional":
            if (vazio && !(tipo === "aluno" && Number(campo?.ordem) === 56))
              erros.push("Campo obrigatório.");
            break;
          case "max_length":
            if (!vazio && v.length > Number(p)) erros.push(`Deve ter no máximo ${p} caracteres.`);
            break;
          case "min_length":
            if (!vazio && v.length < Number(p)) erros.push(`Deve ter no mínimo ${p} caracteres.`);
            break;
          case "fixed_length":
            if (!vazio && campo.tipo !== "select" && v.length !== Number(p))
              erros.push(`Deve ter exatamente ${p} caracteres.`);
            break;
          case "numeric":
            if (!vazio && !/^\d+$/.test(v)) erros.push("Deve ser numérico.");
            break;
          case "email":
            if (!vazio && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) erros.push("E-mail inválido.");
            break;
          case "intervalo":
            if (!vazio && /^\d+$/.test(v)) {
              const [min, max] = String(p)
                .split(",")
                .map((s) => s.trim());
              const n = parseInt(v, 10);
              if (min !== "" && n < parseInt(min, 10)) erros.push(`Deve ser no mínimo ${min}.`);
              else if (max !== "" && n > parseInt(max, 10))
                erros.push(`Deve ser no máximo ${max}.`);
            }
            break;
          case "nulo":
            if (!vazio) erros.push("O campo deve ser nulo.");
            break;
          case "time":
            if (!vazio) {
              const e = this.erroHorario(v);
              if (e) erros.push(e);
            }
            break;
        }
      });

      return erros;
    },

    // Valida horário "hh:mm-hh:mm" igual ao backend (erroTime).
    erroHorario(v) {
      const m = /^(\d{2}):(\d{2})-(\d{2}):(\d{2})$/.exec(v);
      if (!m) return "O horário deve estar no formato hh:mm-hh:mm.";
      const h1 = +m[1];
      const mi1 = +m[2];
      const h2 = +m[3];
      const mi2 = +m[4];
      for (const [h, mi] of [
        [h1, mi1],
        [h2, mi2],
      ]) {
        if (h > 23) return "A hora deve estar entre 00 e 23.";
        if (mi > 59) return "Os minutos devem estar entre 00 e 59.";
        if (mi % 5 !== 0) return "Os minutos devem ser múltiplos de 5.";
      }
      if (h1 * 60 + mi1 >= h2 * 60 + mi2) return "O horário inicial deve ser menor que o final.";
      return null;
    },

    regrasCampo(campo, tipo = null) {
      const regras = {};

      campo?.validacoes.forEach((v) => {
        const code = v?.regra?.code;
        const valor = v?.valor;

        switch (code) {
          case "required":
            regras.required = true;
            break;
          case "conditional":
            // Campo só é renderizado quando campoDependenciaAtendida() é true
            // (sem dep, ou dep satisfeita). Visível => o backend exige. Espelha
            // a regra do ValidarRespostasService (conditional + dep atendida).
            // Exceção: R30/56 (escolaridade) não se aplica a aluno.
            if (!(tipo === "aluno" && Number(campo?.ordem) === 56)) {
              regras.required = true;
            }
            break;
          case "max_length":
            regras.max = valor;
            break;
          case "min_length":
            regras.min = valor;
            break;
          case "fixed_length":
            if (campo.tipo !== "select") regras.length = valor;
            break;
          case "numeric":
            regras.numeric = true;
            break;
          case "email":
            regras.email = true;
            break;
          case "time":
            regras.timeValidator = true;
            break;
          case "in":
            regras.oneOf = valor?.split(",");
            break;
        }
      });

      return regras;
    },

    async buscarCensos() {
      try {
        this.censos = await this.$services.censoService.index();
      } catch {
        this.$toast.danger("Erro ao carregar censos.");
      } finally {
        this.loading = false;
      }
    },

    selecionarCenso(censo) {
      this.censoSelecionado = censo;
      this.etapaSelecionada = null;
      this.etapaDetalhada = null;
    },

    async trocarTipoPessoa() {
      this.pessoaPaginaAtual = 1;
      this.$loaderService.open("Carregando pessoas...");
      await this.$nextTick();
      this.$loaderService.close();
    },

    isTurmaModulo(modulo) {
      return Number(modulo.codigo) === 20;
    },

    isAlunoModulo(modulo) {
      return Number(modulo.codigo) === 60;
    },

    isPessoaFisicaModulo(modulo) {
      return Number(modulo.codigo) === 30;
    },

    isGestorModulo(modulo) {
      return Number(modulo.codigo) === 40;
    },

    isProfessorModulo(modulo) {
      return Number(modulo.codigo) === 50;
    },

    pessoaNome(pessoa) {
      return pessoa.nome || pessoa.nomecompleto || "—";
    },

    // Faixas de ordem que definem as sub-tabs da pessoa física (R30).
    faixasPessoaFisica() {
      return [
        { nome: "Identificação", min: 1, max: 6 },
        { nome: "Dados Pessoais", min: 7, max: 16 },
        { nome: "Deficiências", min: 17, max: 35 },
        { nome: "Recursos", min: 36, max: 49 },
        { nome: "Formação", min: 50, max: 999 },
      ];
    },

    // Índice da sub-tab (faixa) onde o campo de `ordem` aparece, ou -1.
    indiceFaixaCampo(ordem) {
      return this.faixasPessoaFisica().findIndex((f) => ordem >= f.min && ordem <= f.max);
    },

    pessoaFisicaSubTabs(campos) {
      const faixas = this.faixasPessoaFisica();
      return faixas
        .map((f) => ({
          nome: f.nome,
          campos: (campos || []).filter((c) => c.ordem >= f.min && c.ordem <= f.max),
        }))
        .filter((g) => g.campos.length > 0);
    },

    camposPorGrupo(campos) {
      const grupos = [];
      (campos || []).forEach((campo) => {
        const nome = campo.grupo || "Geral";
        let grupo = grupos.find((g) => g.nome === nome);
        if (!grupo) {
          grupo = { nome, campos: [] };
          grupos.push(grupo);
        }
        grupo.campos.push(campo);
      });
      return grupos;
    },

    async selecionarEtapa(etapa) {
      this.etapaSelecionada = etapa;
      this.moduloAtivo = 0;
      this.turmaAtiva = 0;
      this.alunoPaginaAtual = 1;
      this.gestorPaginaAtual = 1;
      this.pessoaFisicaTipo = "professor";
      this.pessoaPaginaAtual = 1;
      this.subTabsPessoas = {};
      this.respostas = {};
      this.respostasAlunos = {};
      this.respostasGestores = {};
      this.respostasPessoas = {};
      this.turmas = [];
      this.alunos = [];
      this.matriculas = [];
      this.gestores = [];
      this.professores = [];
      this.vinculosProfessor = [];
      this.respostasProfessores = {};
      this.loadingEtapa = true;
      this.etapaDetalhada = null;
      try {
        const [etapaDetalhada, turmas] = await Promise.all([
          this.$services.censoService.showEtapa(etapa.id),
          this.$services.turmasService.syncAll(),
        ]);
        this.etapaDetalhada = etapaDetalhada;
        this.turmas = turmas;
        this.alunos = etapaDetalhada.alunos_censo || [];
        this.matriculas = etapaDetalhada.matriculas_censo || [];
        this.professores = etapaDetalhada.professores_censo || [];
        this.vinculosProfessor = etapaDetalhada.vinculos_professor_censo || [];
        this.gestores = etapaDetalhada.gestores_censo || [];

        (this.etapaDetalhada.modulos || []).forEach((modulo) => {
          this.popularRespostasModulo(modulo);
        });
      } catch {
        this.$toast.danger("Erro ao carregar etapa.");
      } finally {
        this.loadingEtapa = false;
      }
    },

    // Preenche o saco de respostas de UM módulo a partir dos valores que vieram do
    // backend (campo.valor_padrao*). Extraído de selecionarEtapa para ser reusado
    // pela sincronização (que recarrega só o módulo com os dados do sistema).
    popularRespostasModulo(modulo) {
      if (this.isTurmaModulo(modulo)) {
        this.turmas.forEach((turma) => {
          this.$set(this.respostas, turma.id, {});
          (modulo.campos || []).forEach((campo) => {
            const valor = campo.valor_padrao_turmas?.[turma.id] ?? campo.valor_padrao ?? null;
            if (valor !== null && valor !== undefined) {
              this.$set(this.respostas[turma.id], campo.id, valor);
            }
          });
        });
      } else if (this.isAlunoModulo(modulo)) {
        // Ids dos campos do próprio módulo Aluno (R60). Uma dependência cujo
        // gatilho NÃO está neste conjunto é cross-registro (Tipo de turma,
        // R20/14) — o gatilho dos campos de AEE (R60/10-20).
        const idsCampos60 = new Set((modulo.campos || []).map((campo) => campo.id));
        const gatilhosCrossRegistro = new Set();
        (modulo.campos || []).forEach((campo) => {
          (campo.dependencias || []).forEach((dep) => {
            if (!idsCampos60.has(dep.campo_gatilho_id)) {
              gatilhosCrossRegistro.add(dep.campo_gatilho_id);
            }
          });
        });

        // Indexado por matrícula (não aluno) — ver `matriculas`.
        this.matriculas.forEach((matricula) => {
          this.$set(this.respostasAlunos, matricula.id, {});
          (modulo.campos || []).forEach((campo) => {
            const valor = campo.valor_padrao_alunos?.[matricula.id] ?? campo.valor_padrao ?? null;
            if (valor !== null && valor !== undefined) {
              this.$set(this.respostasAlunos[matricula.id], campo.id, valor);
            }
          });

          // Injeta os gatilhos cross-registro (Tipo de turma, Etapa, Mediação,
          // Local, País de residência) no saco de respostas da matrícula, cada um
          // com o SEU valor, para campoDependenciaAtendida resolver os campos que
          // dependem deles (AEE R60/10-20, Transporte escolar R60/22, etc.).
          // Antes injetava tipo_turma_codigo em TODOS os gatilhos, o que só resolvia
          // dependências de Tipo de turma e escondia o Transporte (dep. de Mediação
          // e País de residência). Sintético: não é enviado ao salvar.
          const gatilhos = matricula.gatilhos || {};
          gatilhosCrossRegistro.forEach((gatilhoId) => {
            const valor = gatilhos[gatilhoId];
            if (valor !== null && valor !== undefined) {
              this.$set(this.respostasAlunos[matricula.id], gatilhoId, valor);
            }
          });
        });
      } else if (this.isPessoaFisicaModulo(modulo)) {
        this.professores.forEach((p) => {
          this.$set(this.respostasPessoas, `professor_${p.id}`, {});
          this.$set(this.subTabsPessoas, `professor_${p.id}`, 0);
          (modulo.campos || []).forEach((campo) => {
            const vpp = campo.valor_padrao_pessoas?.professor;
            const valor = vpp?.[p.id] ?? campo.valor_padrao ?? null;

            if (valor !== null && valor !== undefined) {
              this.$set(this.respostasPessoas[`professor_${p.id}`], campo.id, valor);
            }
          });
        });
        this.alunos.forEach((a) => {
          this.$set(this.respostasPessoas, `aluno_${a.id}`, {});
          this.$set(this.subTabsPessoas, `aluno_${a.id}`, 0);
          (modulo.campos || []).forEach((campo) => {
            const valor = campo.valor_padrao_pessoas?.aluno?.[a.id] ?? campo.valor_padrao ?? null;
            if (valor !== null && valor !== undefined) {
              this.$set(this.respostasPessoas[`aluno_${a.id}`], campo.id, valor);
            }
          });
        });
        this.gestores.forEach((g) => {
          this.$set(this.respostasPessoas, `gestor_${g.id}`, {});
          this.$set(this.subTabsPessoas, `gestor_${g.id}`, 0);
          (modulo.campos || []).forEach((campo) => {
            const valor = campo.valor_padrao_pessoas?.gestor?.[g.id] ?? campo.valor_padrao ?? null;
            if (valor !== null && valor !== undefined) {
              this.$set(this.respostasPessoas[`gestor_${g.id}`], campo.id, valor);
            }
          });
        });
      } else if (this.isGestorModulo(modulo)) {
        this.gestores.forEach((g) => {
          this.$set(this.respostasGestores, g.id, {});
          (modulo.campos || []).forEach((campo) => {
            const valor = campo.valor_padrao_gestores?.[g.id] ?? campo.valor_padrao ?? null;
            if (valor !== null && valor !== undefined) {
              this.$set(this.respostasGestores[g.id], campo.id, valor);
            }
          });
        });
      } else if (this.isProfessorModulo(modulo)) {
        // R50 por vínculo (professor + turma): respostas chaveadas por v.key.
        // Ids dos campos do próprio módulo Docente (R50). Uma dependência cujo
        // gatilho NÃO está neste conjunto é cross-registro (Etapa agregada da
        // turma, R20/23) — o gatilho dos campos de Novo Ensino Médio (R50/34-38).
        const idsCampos50 = new Set((modulo.campos || []).map((campo) => campo.id));
        const gatilhosCrossRegistro = new Set();
        (modulo.campos || []).forEach((campo) => {
          (campo.dependencias || []).forEach((dep) => {
            if (!idsCampos50.has(dep.campo_gatilho_id)) {
              gatilhosCrossRegistro.add(dep.campo_gatilho_id);
            }
          });
        });

        this.vinculosProfessor.forEach((v) => {
          this.$set(this.respostasProfessores, v.key, {});
          (modulo.campos || []).forEach((campo) => {
            const valor = campo.valor_padrao_professores?.[v.key] ?? campo.valor_padrao ?? null;
            if (valor !== null && valor !== undefined) {
              this.$set(this.respostasProfessores[v.key], campo.id, valor);
            }
          });

          // Injeta o gatilho cross-registro (Etapa agregada da turma) no saco de
          // respostas do vínculo, para campoDependenciaAtendida resolver os
          // campos de Novo Ensino Médio. Sintético: não é enviado ao salvar
          // (filtrarRespostas usa só campos do módulo).
          if (v.etapa_agregada_codigo != null) {
            gatilhosCrossRegistro.forEach((gatilhoId) => {
              this.$set(this.respostasProfessores[v.key], gatilhoId, v.etapa_agregada_codigo);
            });
          }
        });
      } else {
        // Módulo nível-escola (R00/R10). Reaplica sempre (inclusive null) para que a
        // sincronização limpe valores que deixaram de existir no sistema.
        (modulo.campos || []).forEach((campo) => {
          this.$set(this.respostas, campo.id, campo.valor_padrao ?? null);
        });
      }
    },

    // Botão "Sincronizar com o sistema": recarrega SÓ este módulo trazendo os campos
    // com origem no sistema de acordo com o sistema atual, sobrescrevendo o que já
    // estava preenchido. O padrão continua sendo "valor salvo tem prioridade"; aqui o
    // usuário opta explicitamente por puxar do sistema.
    sincronizarModulo(modulo) {
      this.$modals.warn({
        title: "Sincronizar com o sistema?",
        message: `Os campos do módulo (${modulo.descricao}) que têm origem no sistema serão reescritos com os dados atuais do sistema, substituindo o que está preenchido na tela. Salve depois para persistir.`,
        buttons: [
          {
            text: "Cancelar",
            callback: () => this.$modals.close(),
            props: { color: "warning", small: true },
          },
          {
            text: "Sincronizar",
            callback: async () => {
              this.$modals.close();
              this.$loaderService.open("Sincronizando com o sistema...");
              try {
                const etapaSincronizada = await this.$services.censoService.showEtapa(
                  this.etapaSelecionada.id,
                  modulo.codigo
                );
                const moduloSincronizado = (etapaSincronizada.modulos || []).find(
                  (m) => Number(m.codigo) === Number(modulo.codigo)
                );
                if (moduloSincronizado) {
                  this.popularRespostasModulo(moduloSincronizado);
                }
                this.$toast.success("Módulo sincronizado com o sistema.");
              } catch (error) {
                this.$handleError(error);
              }
              this.$loaderService.close();
            },
            props: { color: "primary", small: true },
          },
        ],
      });
    },

    // Rótulo legível do tipo de pessoa do módulo Pessoa Física (30).
    rotuloTipoPessoa(tipo) {
      return { professor: "Professores", aluno: "Alunos", gestor: "Gestores" }[tipo] || "pessoas";
    },

    // Botão "Excluir dados": soft-delete de todas as respostas do módulo (ou, no
    // módulo Pessoa Física, só do tipo informado) neste censo/escola. Confirmação
    // por digitação (padrão $modals.danger). Após excluir, recarrega a etapa para
    // refletir os campos limpos.
    excluirDadosModulo(modulo, tipo = null) {
      const escopo = tipo ? `de ${this.rotuloTipoPessoa(tipo)} ` : "";
      this.$modals.danger({
        title: "Cuidado!",
        // message: `Isto apagará todos os dados ${escopo}deste módulo (${modulo.descricao}) neste censo. Esta ação não pode ser desfeita pela tela.`,
        message: `Os dados do módulo (${modulo.descricao}) serão apagados da base de dados, ficando disponíveis visualmente apenas os extraídos do sistema`,
        confirmationCode: "EXCLUIR",
        confirmationMessage: "Digite <strong>EXCLUIR</strong> para confirmar",
        buttons: [
          {
            text: "Cancelar",
            callback: () => this.$modals.close(),
            props: { color: "warning", small: true },
          },
          {
            text: "Confirmar",
            disableIfCodeDoesntMatch: true,
            callback: async () => {
              this.$modals.close();
              this.$loaderService.open("Excluindo dados...");
              try {
                await this.$services.censoService.excluirRespostasModulo(modulo.id, tipo);
                // Preserva a aba de módulo aberta ao recarregar (selecionarEtapa
                // reseta moduloAtivo para 0).
                const moduloAtivoAtual = this.moduloAtivo;
                await this.selecionarEtapa(this.etapaSelecionada);
                this.moduloAtivo = moduloAtivoAtual;
                this.$toast.success("Dados excluídos com sucesso.");
              } catch (error) {
                this.$handleError(error);
              }
              this.$loaderService.close();
            },
            props: { color: "error", small: true },
          },
        ],
      });
    },

    async salvarComValidacao(validate, fn) {
      const ok = await validate();
      if (!ok) {
        this.$toast.danger("Preencha os campos obrigatórios antes de salvar.");
        return;
      }
      await fn();
    },

    async salvarRespostas(modulo, turma = null, aluno = null, pessoa = null, gestor = null) {
      const key = turma
        ? `${modulo.id}-turma-${turma.id}`
        : aluno
        ? `${modulo.id}-aluno-${aluno.id}`
        : pessoa
        ? `${modulo.id}-${this.pessoaFisicaTipo}-${pessoa.id}`
        : gestor
        ? `${modulo.id}-gestor-${gestor.id}`
        : modulo.id;
      this.salvandoModulo = key;

      // Limpa erros do render anterior (módulo normal) antes de revalidar.
      if (!turma && !aluno && !pessoa && !gestor && modulo?.campos) {
        modulo.campos.forEach((campo) => this.$set(campo, "erros", []));
      }

      try {
        let payload;

        if (turma) {
          payload = {
            turma_id: turma.id,
            respostas: this.filtrarRespostas(modulo, this.respostas[turma.id] || {}),
          };
        } else if (aluno) {
          // Módulo 60: `aluno` aqui é, na prática, um registro de `matriculas`
          // (1 por vínculo) — ver `matriculas`/`matriculas_censo`.
          payload = {
            matricula_id: aluno.id,
            respostas: this.filtrarRespostas(modulo, this.respostasAlunos[aluno.id] || {}),
          };
        } else if (pessoa) {
          const pessoaKey = `${this.pessoaFisicaTipo}_${pessoa.id}`;
          payload = {
            pessoa_tipo: this.pessoaFisicaTipo,
            pessoa_id: pessoa.id,
            respostas: this.filtrarRespostas(modulo, this.respostasPessoas[pessoaKey] || {}),
          };
        } else if (gestor) {
          payload = {
            gestor_id: gestor.id,
            respostas: this.filtrarRespostas(modulo, this.respostasGestores[gestor.id] || {}),
          };
        } else {
          payload = { respostas: this.filtrarRespostas(modulo, this.respostas) };
        }

        console.log("payload:", JSON.stringify(payload));
        await this.$services.censoService.salvarModulo(modulo.id, payload);
        this.$toast.success("Salvo com sucesso.");
      } catch (e) {
        const erros = this.extrairErros(e);
        if (erros) {
          // Mapeia erros (chaveados por nome do campo) para o estado de erro por id
          // de cada aba (turma/aluno/pessoa/gestor) ou para campo.erros no módulo
          // normal — destaca os campos em vermelho e navega até a sub-aba certa.
          if (turma) {
            this.$set(this.errosTurma, turma.id, this.mapearErrosBackend(modulo, erros));
          } else if (aluno) {
            this.$set(this.errosAlunos, aluno.id, this.mapearErrosBackend(modulo, erros));
          } else if (pessoa) {
            const pessoaKey = `${this.pessoaFisicaTipo}_${pessoa.id}`;
            const mapa = this.mapearErrosBackend(modulo, erros);
            this.$set(this.errosPessoas, pessoaKey, mapa);
            const campoComErroId = Object.keys(mapa)[0];
            const campoComErro = modulo.campos?.find((c) => c.id === parseInt(campoComErroId));
            if (campoComErro) {
              const indiceFaixa = this.indiceFaixaCampo(campoComErro.ordem);
              if (indiceFaixa !== -1) this.$set(this.subTabsPessoas, pessoaKey, indiceFaixa);
            }
          } else if (gestor) {
            this.$set(this.errosGestores, gestor.id, this.mapearErrosBackend(modulo, erros));
          } else if (modulo?.campos) {
            modulo.campos.forEach((campo) => {
              this.$set(campo, "erros", erros[campo.nome] || []);
            });
          }
          // Um toast por erro — a mensagem já contém nome e código do campo.
          Object.values(erros)
            .flat()
            .forEach((msg) => this.$toast.danger(msg));
        } else {
          this.$toast.danger("Erro ao salvar.");
        }
      } finally {
        this.salvandoModulo = null;
      }
    },
  },
};
</script>

<style scoped>
.pendencia-clicavel {
  cursor: pointer;
  transition: filter 0.15s ease;
}
.pendencia-clicavel:hover {
  filter: brightness(0.95);
  text-decoration: underline;
}

.destaque-pendencia {
  animation: destaque-flash 2.6s ease-out;
}
@keyframes destaque-flash {
  0% {
    box-shadow: 0 0 0 4px rgba(255, 82, 82, 0.7);
    background-color: rgba(255, 82, 82, 0.16);
  }
  70% {
    box-shadow: 0 0 0 4px rgba(255, 82, 82, 0.4);
    background-color: rgba(255, 82, 82, 0.08);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 82, 82, 0);
    background-color: transparent;
  }
}

.destaque-campo {
  border-radius: 6px;
  animation: destaque-campo-flash 2.6s ease-out;
}
@keyframes destaque-campo-flash {
  0% {
    box-shadow: 0 0 0 2px #ff5252, 0 0 10px 2px rgba(255, 82, 82, 0.55);
    background-color: rgba(255, 82, 82, 0.12);
  }
  70% {
    box-shadow: 0 0 0 2px rgba(255, 82, 82, 0.6);
    background-color: rgba(255, 82, 82, 0.06);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 82, 82, 0);
    background-color: transparent;
  }
}
</style>
